// SearchOrder.js (النسخة المحسنة مع أزرار نسخ الرسائل)
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../utils/auth";
import { searchOrdersByPhone, updateOrderStatus, revertToNew, deleteOrder } from "../api/OrderAPIs";
import { fetchDesignById } from "../api/DesignAPIs";
import { fetchTypeById } from "../api/TypeAPIs";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL_V2 } from "../api/URL_Header";

const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[...Array(3)].map((_, index) => (
          <div key={index} className="flex gap-4 border-b py-3">
            <div className="w-12 h-12 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const SearchOrders = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [phoneInput, setPhoneInput] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);

  React.useEffect(() => {
    if (authState.role !== "Admin" && authState.role !== "Moderator") {
      navigate("/login");
    }
  }, [authState, navigate]);

  // استبدل الدالة القديمة بهذه
  const normalizePhoneNumber = (phone) => {
    if (!phone) return phone;

    let cleaned = phone.replace(/[^\d+]/g, '');

    if (cleaned.startsWith('+')) {
      cleaned = cleaned.slice(1);
    }

    if (cleaned.startsWith('20') && cleaned.length === 12) {
      cleaned = '0' + cleaned.slice(2);
    }

    if (cleaned.startsWith('2') && cleaned.length === 11) {
      cleaned = '0' + cleaned;
    }

    if (cleaned.length === 10 && /^1[0125]/.test(cleaned)) {
      cleaned = '0' + cleaned;
    }

    return /^01[0125]\d{8}$/.test(cleaned) ? cleaned : phone.replace(/\D/g, '');
  };

  const handleDownload = async (url, filename = "design.jpg") => {
    try {
      const response = await fetch(
        `${API_BASE_URL_V2}/api/files/download?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(filename)}`
      );

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed:", err);
      alert("فشل التحميل. تحقق من السيرفر.");
    }
  };

    const enrichOrders = async (data) => {
      return await Promise.all(
        data.map(async (order) => {
          let enrichedItems = [];

          for (const detail of order.orderDetails) {
            const design = await fetchDesignById(detail.designId);
            const type = await fetchTypeById(detail.typeId);
            if (!design || !type) continue;

            const baseItem = {
              id: detail.designId,
              name: design.Name,
              price: detail.price,
              type: type.name,
              driveLink: design.code.startsWith("CUSTOM") 
                ? `${API_BASE_URL_V2}${design.driveLink}` 
                : design.driveLink || "",
              code: design.code || "None",
            };

            // نكرر المنتج حسب الكمية
            for (let i = 0; i < detail.quantity; i++) {
              enrichedItems.push({
                ...baseItem,
                quantityIndex: i + 1,           // رقم القطعة الحالية (1، 2، ...)
                totalQuantity: detail.quantity, // إجمالي الكمية
              });
            }
          }

          return {
            orderNumber: order.orderId,
            items: enrichedItems,
            total: order.totalAmount,
            profit: order.profit || 0,
            date: new Date(order.date).toISOString().split("T")[0],
            address: order.address?.fullAddress || "غير معروف",
            state: order.address?.state || "غير معروف",
            phoneNumber: order.phoneNumber || "غير معروف",
            phoneNumber2: order.phoneNumber2 || "غير معروف",
            name: order.name || "غير معروف",
            note: order.note || "",
            status: order.status || "غير معروف",
          };
        })
      );
    };

  const handleSearch = async () => {
    if (!phoneInput.trim()) {
      setSearchError("يرجى إدخال رقم هاتف.");
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    setSearchResults([]);

    try {
      const normalizedPhone = normalizePhoneNumber(phoneInput);
      const rawResults = await searchOrdersByPhone(normalizedPhone);
      const enrichedResults = await enrichOrders(rawResults);
      setSearchResults(enrichedResults);
      toast.success("تم البحث بنجاح");
    } catch (err) {
      setSearchError("لا يوجد اوردر بالرقم ده");
      // toast.error(" لا يوجد اوردر بالرقم ده");
    } finally {
      setIsSearching(false);
    }
  };

  const refreshSearch = async () => {
    if (phoneInput.trim()) {
      try {
        const normalizedPhone = normalizePhoneNumber(phoneInput);
        const rawResults = await searchOrdersByPhone(normalizedPhone);
        const enrichedResults = await enrichOrders(rawResults);
        setSearchResults(enrichedResults);
      } catch (err) {
        setSearchError(err.message);
        toast.error("فشل في إعادة تحميل النتائج: " + err.message);
      }
    }
  };

  const handleUpdateStatus = async (id, status) => {
    let reason = "no reason (normal order)";
    if (status === "Hold") {
      reason = prompt("يرجى إدخال سبب وضع الطلب على الانتظار:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب لوضع الطلب على الانتظار.");
        return;
      }
    } else if (status === "Changes") {
      reason = prompt("يرجى إدخال سبب التغيير:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب التغيير.");
        return;
      }
    }else if (status === "Under Purchase") {
      reason = prompt("ادخل الاوردر ريفرنس Girabtcom.xxx:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب التغيير.");
        return;
      }
    }
    else if (status === "Spam") {
      reason = prompt("يرجى إدخال سبب :");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب .");
        return;
      }
    }
    // else if (status === "Deposit Required") {
    //   reason = prompt("يرجى إدخال سبب :");
    //   if (reason === null) return;
    //   if (!reason.trim()) {
    //     toast.error("يجب إدخال سبب .");
    //     return;
    //   }
    // }


    const confirmUpdate = window.confirm(`هل أنت متأكد من تحديث الحالة إلى ${status}؟`);
    if (!confirmUpdate) return;

    try {
      await updateOrderStatus(id, status, reason);
      toast.success(`تم تحديث الطلب إلى ${status}`);
      await refreshSearch();
    } catch (err) {
      toast.error("فشل في تحديث الطلب: " + err.message);
    }
  };

  const handleRevertToNew = async (id) => {
    try {
      await revertToNew(id);
      toast.success("تم إعادة الطلب إلى جديد");
      await refreshSearch();
    } catch (err) {
      toast.error("فشل في إعادة الطلب: " + err.message);
    }
  };

  const handleDeleteOrder = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا الطلب؟")) return;

    try {
      await deleteOrder(id);
      toast.success("تم حذف الطلب بنجاح");
      await refreshSearch();
    } catch (err) {
      toast.error("فشل في حذف الطلب: " + err.message);
    }
  };

  const handleCopyOrderInfo = (order) => {
    const phone = `${order.phoneNumber}-${order.phoneNumber2}`;
    const formattedState = `${order.state} (C)`;
    const text = `${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD`;

    navigator.clipboard.writeText(text).then(() => {
      toast.success("تم نسخ معلومات العميل!");
    }).catch(() => {
      toast.error("فشل في النسخ.");
    });
  };

  const handleCopyItems = (order) => {
    const lines = order.items.map((item, index) => {
      const itemNum = `${index + 1}-${order.items.length}`;
      const type = item.type || "Unknown Type";
      const driveLink = item.driveLink || "No Link";
      return `${itemNum}\t\tPhone Cover (H)\t${type}\t\t1\t${driveLink}`;
    }).join('\n');

    navigator.clipboard.writeText(lines).then(() => {
      toast.success("تم نسخ المنتجات!");
    }).catch(() => {
      toast.error("فشل في النسخ.");
    });
  };
  const copyToClipboard = (text) => {
  // إذا كان المتصفح يدعم clipboard API (مثل Chrome على الكمبيوتر أو HTTPS)
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text).then(() => {
      toast.success("تم النسخ بنجاح!");
    }).catch(() => {
      fallbackCopy(text);
    });
  } else {
    // بديل للموبايل والبيئات غير الآمنة
    fallbackCopy(text);
  }
};

const fallbackCopy = (text) => {
  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.style.position = "fixed";
  textArea.style.opacity = "0";
  textArea.style.left = "-999999px";
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    const successful = document.execCommand('copy');
    if (successful) {
      toast.success("تم النسخ بنجاح!");
    } else {
      toast.error("فشل النسخ، حاول مرة أخرى.");
    }
  } catch (err) {
    toast.error("فشل النسخ، حاول النسخ يدويًا.");
    console.error("Copy failed:", err);
  }

  document.body.removeChild(textArea);
};
  // 1. رسالة التأكيد (مع تفاصيل الطلب)
  const handleCopyConfirmationMessage = (order) => {
    const itemsList = order.items.map(item => `Types : ${item.type} - Code : ${item.code}`).join('\n');
    const message = `أهلا بحضرتك" 🌸
مع حضرتك فاطمة من فريق جرابتكم ستور 💛
بنراجع مع حضرتك الطلب قبل بدء تنفيذه، لأن التصميمات بتتعمل مخصوص لحضرتك حسب الطلب 🎨✨

تفاصيل الطلب:
${itemsList}

بمجرد تأكيد حضرتك هنبدأ تنفيذ وتجهيز الأوردر فورًا 🚚⚡

لتأكيد الطلب بشكل نهائي برجاء الرد بــ:
( نعم - تأكيد ) ✅

وفي حالة الرغبة في التعديل أو الإلغاء برجاء إبلاغنا قبل البدء 💬
شكرًا لاختيار حضرتك جرابتكم 🤍🌷`;

    copyToClipboard(message);
  };

  // 2. رسالة بوسطة (ثابتة)
  const handleCopyBostaMessage = () => {
    const message = `أهلاً وسهلاً!

بنحب نبلغ حضرتك إن شركة الشحن هتتواصل مع حضرتك على الواتساب خلال وقت قصير لتأكيد العنوان واستلام الطلب ✅

تنويه مهم:
في رسالة التأكيد اللي هتوصلك ممكن تلاقي اسم شركة مختلفة (مثل TeePlus) 💼  

كمان عدد القطع ممكن يظهر 1 حتى لو كانت اكتر من قطعة واحدة 🧾

يُرجى الرد على رسالة شركة الشحن علشان يتم تأكيد الطلب ويتم بدءالشحن ✉️⚡  

شكرًا لاختيارك جرابتكم 🌷`;

    copyToClipboard(message);
  };

  // 3. رسالة الاعتذار (ثابتة)
  const handleCopyApologyMessage = () => {
    const message = `أهلاً بحضرتك 🌸
مع حضرتك فاطمة من جرابتكم ستور 💛

بنعتذر جداً عن التأخير في تنفيذ الطلب الفترة اللي فاتت،
كان فيه نقص في نوع الجراب المطلوب، لكن الحمد لله تم توفيره الآن وجاهز للتنفيذ ✅

هل نبدأ في تجهيز الطلب فوراً؟ 

نقدّر جداً صبرك وذوقك ❤️
فاطمة - فريق جرابتكم`;

    copyToClipboard(message);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6" dir="rtl">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">البحث عن الطلبات</h1>
          <button
            onClick={() => navigate("/dashboard")}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
          >
            العودة
          </button>
        </div>

        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">البحث برقم الهاتف</h2>
          <div className="flex items-center mb-4">
            <input
              type="text"
              value={phoneInput}
              onChange={(e) => setPhoneInput(e.target.value)}
              placeholder="أدخل رقم الهاتف"
              className="border border-gray-300 rounded px-4 py-2 flex-1"
              dir="rtl"
            />
            <button
              onClick={handleSearch}
              disabled={isSearching}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 mr-2"
            >
              {isSearching ? "جاري..." : "بحث"}
            </button>
          </div>

          {searchError && (
            <div className="bg-red-100 text-red-700 p-4 rounded mb-4">{searchError}</div>
          )}

          {/* أزرار نسخ الرسائل الثابتة */}
          {searchResults.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="text-center">
                <button
                  onClick={() => handleCopyConfirmationMessage(searchResults[0])}
                  className="bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 w-full"
                >
                  📝 نسخ رسالة التأكيد
                </button>
              </div>
              <div className="text-center">
                <button
                  onClick={handleCopyBostaMessage}
                  className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 w-full"
                >
                  🚚 نسخ رسالة بوسطة
                </button>
              </div>
              <div className="text-center">
                <button
                  onClick={handleCopyApologyMessage}
                  className="bg-orange-600 text-white px-3 py-2 rounded hover:bg-orange-700 w-full"
                >
                  😔 نسخ الاعتذار
                </button>
              </div>
            </div>
          )}

          {isSearching ? (
            <SkeletonLoader type="table" />
          ) : searchResults.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full table-auto text-lg">
                <thead>
                  <tr className="bg-gray-100 text-right">
                    <th className="p-2 font-semibold min-w-[100px]">بيانات العميل</th>
                    <th className="p-2 font-semibold min-w-[50px]">رقم</th>
                    <th className="p-2 font-semibold">المنتجات</th>
                    <th className="p-2 font-semibold">رابط الدرايف</th>
                    <th className="p-2 font-semibold">الحالة</th>
                    <th className="p-2 font-semibold">ملاحظات</th>
                    <th className="p-2 font-semibold">نسخ</th>
                    <th className="p-2 font-semibold">إجراءات</th>
                    <th className="p-2 font-semibold">حذف</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.map((order) => (
                    <tr key={order.orderNumber} className="border-b hover:bg-gray-50">
                      {/* عمود واحد لبيانات العميل */}
                      <td className="p-3 align-top text-xs leading-relaxed">
                        <div className="space-y-1">
                          <div><strong>الاسم:</strong> {order.name}</div>
                          <div><strong>الهاتف:</strong> 
                            <a
                              href={`https://wa.me/${normalizePhoneNumber(order.phoneNumber).replace(/^0/, '20')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-green-600 hover:underline ml-1"
                            >
                              {order.phoneNumber}
                            </a>
                            {order.phoneNumber2 !== "غير معروف" && (
                              <>
                                {' - '}
                                <a
                                  href={`https://wa.me/${normalizePhoneNumber(order.phoneNumber2).replace(/^0/, '20')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-green-600 hover:underline"
                                >
                                  {order.phoneNumber2}
                                </a>
                              </>
                            )}
                          </div>
                          <div><strong>العنوان:</strong> {order.address}</div>
                          <div><strong>المحافظة:</strong> {order.state}</div>
                          <div><strong>الإجمالي:</strong> LE {order.total}</div>
                          <div><strong>التاريخ:</strong> {order.date}</div>
                        </div>
                      </td>

                      {/* رقم المنتج */}
                      <td className="p-2 text-center">
                        {order.items.map((item, index) => (
                          <div key={index} className="mb-1">
                            {item.quantityIndex}-{item.totalQuantity}
                          </div>
                        ))}
                      </td>

                      {/* المنتجات */}
                      <td className="p-2 text-right">
                        {order.items.map((item) => (
                          <div key={item.id} className="mb-3 text-xs">
                            <div className="text-sm"><strong>{item.name}</strong> ({item.type})</div>
                          </div>
                        ))}
                      </td>

                      {/* رابط الدرايف */}
                      <td className="p-2 text-right text-xs">
                        {order.items.map((item) => (
                          <div key={item.id} className="mb-3">
                            {item.driveLink ? (
                              <>
                                <a href={item.driveLink} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
                                  رابط
                                </a>
                                {item.code.startsWith("CUSTOM") && (
                                  <button
                                    onClick={() => handleDownload(item.driveLink, `${order.orderNumber}-${item.id}.jpg`)}
                                    className="block mt-1 bg-green-600 text-white text-xs px-2 py-1 rounded hover:bg-green-700"
                                  >
                                    تحميل
                                  </button>
                                )}
                                <div className="text-gray-500 text-sm">{item.code}</div>
                              </>
                            ) : (
                              <span className="text-gray-400">لا يوجد</span>
                            )}
                          </div>
                        ))}
                      </td>

                      {/* الحالة */}
                      <td className="p-2 text-center font-medium">{order.status}</td>

                      {/* الملاحظات */}
                      <td className="p-2 text-xs">{order.note || "—"}</td>

                      {/* أزرار النسخ المحدثة */}
                      <td className="p-2 text-center">
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => handleCopyOrderInfo(order)}
                            className="bg-green-600 text-white text-xs px-5 py-3 rounded hover:bg-green-700"
                          >
                          بيانات العميل
                          </button>
                          <button
                            onClick={() => handleCopyItems(order)}
                            className="bg-green-600 text-white text-xs px-5 py-3 rounded hover:bg-green-700"
                          >
                            بيانات المنتجات
                          </button>
                          {/* <button
                            onClick={() => handleCopyConfirmationMessage(order)}
                            className="bg-green-500 text-white text-xs px-2 py-1 rounded hover:bg-green-600"
                          >
                            التأكيد
                          </button> */}
                        </div>
                      </td>

                      {/* إجراءات */}
                      <td className="p-2 text-center">
                        <div className="flex flex-col gap-1 text-xs">
   
                          {order.status === "Under Purchase" && (
                            <>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Old")}
                                className="bg-blue-500 text-white rounded hover:bg-blue-600 text-sm px-3 py-4 font-medium"
                              >
                                تحديث إلى قديم
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Waiting Confirming")}
                                className="bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
                              >
                                الانتظار للتأكيد
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Confirming")}
                                className="bg-green-600 text-white px-3 py-4 rounded hover:bg-green-700"
                              >
                               وافق ينفذ
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Canceled")}
                                className="bg-red-600 text-white px-3 py-4 rounded hover:bg-red-700"
                              >
                               لم يوافق هيكنسل
                              </button>
                            </>
                          )}
                          {order.status === "Waiting Confirming" && (
                            <>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Confirming")}
                                className="bg-green-600 text-white px-3 py-4 rounded hover:bg-green-700"
                              >
                               وافق ينفذ
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Canceled")}
                                className="bg-red-600 text-white px-3 py-4 rounded hover:bg-red-700"
                              >
                               لم يوافق هيكنسل
                              </button>
                            </>
                          )}
                          <>
                            <button
                              onClick={() => handleUpdateStatus(order.orderNumber, "Old")}
                              className="bg-gray-500 text-white rounded hover:bg-gray-600 text-sm px-3 py-4 font-medium"
                            >
                              تحديث إلى قديم
                            </button>
                            <button
                              onClick={() => handleUpdateStatus(order.orderNumber, "Confirmed")}
                              className="bg-blue-600 text-white px-3 py-4 rounded hover:bg-blue-700"
                            >
                              تأكيد
                            </button>
                            <button
                              onClick={() => handleUpdateStatus(order.orderNumber, "Hold")}
                              className="bg-yellow-600 text-white px-3 py-4 rounded hover:bg-yellow-700"
                            >
                              معلق
                            </button>
                            <button
                              onClick={() => handleUpdateStatus(order.orderNumber, "Changes")}
                              className="bg-purple-600 text-white px-3 py-4 rounded hover:bg-purple-700"
                            >
                              تغييرات
                            </button>
                            <button
                              onClick={() => handleUpdateStatus(order.orderNumber, "Under Purchase")}
                              className="bg-orange-600 text-white px-3 py-4 rounded hover:bg-orange-700"
                            >
                              تحت الشراء
                            </button>
                            <button
                              onClick={() => handleUpdateStatus(order.orderNumber, "New")}
                              className="bg-green-600 text-white px-3 py-4 rounded hover:bg-green-700"
                            >
                              اعاده الي جديد
                            </button>
                            <button
                              onClick={() => handleUpdateStatus(order.orderNumber, "Deposit Required")}
                              className="bg-orange-500 text-white rounded hover:bg-orange-600 text-sm px-3 py-2 font-medium"
                            >
                              ديبوزت
                            </button>
                          </>
                        </div>
                        
                      </td>
                      <td className="p-2 text-center">
                        <div className="flex flex-col gap-1 text-xs">
                        <button
                          onClick={() => handleDeleteOrder(order.orderNumber)}
                          className="bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700"
                        >
                          حذف
                        </button>
                        <button
                          onClick={() => handleUpdateStatus(order.orderNumber, "Spam")}
                          className="bg-red-500 text-white rounded hover:bg-red-600 text-sm px-3 py-2 font-medium"
                        >
                            Spam
                        </button>
                        </div>

                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : phoneInput && !isSearching && !searchError ? (
            <div className="text-gray-600 p-4 text-center">لا توجد طلبات مطابقة.</div>
          ) : null}
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default SearchOrders;